import {
  Button,
  SkeletonLoader,
  Tooltip,
  TooltipTrigger,
} from '@toggle/design-system';
import { useDebounce } from '@toggle/helpers';
import { ConversationSearchResult } from '@toggle/toggle';
import React, { UIEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchModalBase } from '~/components/search-modal-base/SearchModalBase';
import { QUERY_DELAY } from '~/config/constants';

import { ChatCard } from '../chat-card/ChatCard';
import { useFetchInfiniteSearchConversation } from '../hooks/useFetchInfiniteConversation';
import { useReasonerParams } from '../hooks/useReasonerParams';
import { useChat } from '../use-chat/useChat';
import styles from './SearchChat.module.scss';

const SCROLL_OFFSET = 20;

export const isConversationSearchResult = (
  item: unknown
): item is ConversationSearchResult => {
  return item !== null && typeof item === 'object';
};

export const SearchChat = ({
  isHistoryExpanded,
  setIsOpen,
  isOpen,
}: {
  isHistoryExpanded?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}) => {
  const { t } = useTranslation(['chat', 'widget']);

  const [searchString, setSearchString] = useState<string>();
  const debouncedQueryString = useDebounce(searchString ?? '', QUERY_DELAY);

  const getActiveConversationTasks = useChat(
    state => state.getActiveConversationTasks
  );

  const { setReasonerParams } = useReasonerParams();

  const handleModalClose = () => {
    setSearchString(undefined);
    setIsOpen(false);
  };

  const { data, isLoading, hasNextPage, fetchNextPage } =
    useFetchInfiniteSearchConversation({
      queryString: debouncedQueryString,
      shouldFetch: isOpen,
    });

  const handleResultClick = (itemId: string) => {
    setReasonerParams({
      conversationId: itemId,
    });

    getActiveConversationTasks(itemId);
    handleModalClose();
  };

  const renderLoadingNode = () => {
    return (
      <div>
        <div className={styles.skeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '200px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '360px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
        <div className={styles.skeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '200px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '360px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
      </div>
    );
  };

  const searchResult = data?.pages.flatMap(page => page) || [];

  const handleRefetch = (e: UIEvent<HTMLElement>) => {
    const element = e.target;

    if (element instanceof HTMLElement) {
      const isScrollToBottom =
        element.scrollTop + element.clientHeight >=
        element.scrollHeight - SCROLL_OFFSET;

      if (isScrollToBottom && !isLoading && hasNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <>
      <Tooltip
        label={t('chat:historySidebar.searchConversation')}
        disabled={isHistoryExpanded}
        trigger={TooltipTrigger.Hover}
        placement="right"
      >
        <Button
          className={styles.styledButton}
          variant={'empty'}
          size="small"
          iconName="Search"
          aria-label={t('chat:historySidebar.searchConversation')}
          onClick={() => setIsOpen(!isOpen)}
        />
      </Tooltip>
      {isOpen && (
        <div
          onScrollCapture={(e: UIEvent<HTMLDivElement>) => {
            handleRefetch(e);
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className={styles.searchModalWrap}>
            <SearchModalBase
              title={t('chat:searchChats')}
              noSearchResultsLabel={t('widget:search.noSearchResults')}
              renderDropdownItem={item => {
                const isSearchResult = isConversationSearchResult(item);

                return (
                  <>
                    {isLoading
                      ? renderLoadingNode()
                      : isSearchResult && (
                          <ChatCard
                            title={item.name}
                            summary={item.state.content}
                            updatedAt={item.updated_at}
                            searchString={searchString}
                          />
                        )}
                  </>
                );
              }}
              beforeResultsNode={
                <>
                  {!isLoading && !!searchResult.length && (
                    <h4 className={styles.searchDescription}>
                      {t('chat:chatsSorted')}
                    </h4>
                  )}
                </>
              }
              searchValue={searchString || ''}
              handleSearch={e => {
                setSearchString(e);
              }}
              selectItem={item => {
                const isSearchResult = isConversationSearchResult(item);

                if (isSearchResult) {
                  handleResultClick(item.conversation_id);
                }
              }}
              dropdownItems={searchResult}
              isLoading={false}
              isOpen={isOpen}
              onClose={handleModalClose}
              loadingNode={<></>}
              shouldResetScrollOnItemsChange={false}
            />
          </div>
        </div>
      )}
    </>
  );
};
